import type { IdvStatus } from '@common/enums';
import type { StandaloneIDVEntry } from '@common/types/standaloneIdv';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AlertDialog from '@/components/AlertDialog';
import LoadingButton from '@/components/Buttons/LoadingButton';
import DataTable, { type Column } from '@/components/DataTable';
import IDVModal from '@/components/IDVModal';
import PageWrapper from '@/components/PageWrapper';
import { useLawFirmContext } from '@/hooks/useLawFirmContext';
import { invalidateSAIDVList, useSAIDVs } from '@/queries/saidv';
import { deleteSaIdv } from '@/services/IDVService';
import { theme } from '@/theme';
import { convertUTCDateToEST, truncate } from '@/utils';
import { successSnackbar } from '@/utils/snackbar';

const STATUS_OPTIONS = [
    {
        label: 'Request Pending',
        name: 'Request Pending',
        color: theme.palette.neutral.N_500,
        backgroundColor: theme.palette.neutral.N_100,
    },
    {
        label: 'Review Report',
        name: 'Review Report',
        color: '#389224',
        backgroundColor: theme.palette.success.S_50,
    },
    {
        label: 'Cancelled',
        name: 'Cancelled',
        color: theme.palette.error.main,
        backgroundColor: '#f8d7da',
    },
];

const STATUS_PRIORITIES: Record<IdvStatus, number> = {
    'Request Pending': 3,
    'Review Report': 2,
    Cancelled: 1,
};

const STATUS_COLORS: Record<IdvStatus, string> = {
    'Request Pending': '#FFB300',
    Cancelled: '#F44336',
    'Review Report': '#4CAF50',
};

interface SAIDVFilterConfig {
    searchKey: string;
    status: string[];
}

export default function StandAloneIDV() {
    const queryClient = useQueryClient();
    const [filterConfig, setFilterConfig] = useState<SAIDVFilterConfig>({
        searchKey: '',
        status: [],
    });

    const clearFilterConfig = (key: string, value?: string) => {
        setFilterConfig((prev) => {
            if (key === 'status' && value) {
                return {
                    ...prev,
                    status: prev.status.filter((s) => s !== value),
                };
            }
            const isArray = Array.isArray(prev[key as keyof SAIDVFilterConfig]);
            return {
                ...prev,
                [key]: isArray ? [] : '',
            };
        });
    };

    const { activeLawFirmId } = useLawFirmContext();

    const lawFirmIds = useMemo(() => {
        if (activeLawFirmId) return [activeLawFirmId];
        return [];
    }, [activeLawFirmId]);

    const { data: saidvList = [], isLoading } = useSAIDVs(lawFirmIds);

    const filteredIDV = useMemo(
        () =>
            saidvList.filter((clientInfo) => {
                const statuses = clientInfo?.treefortRequestStatuses || [];
                const finalStatus =
                    statuses.length > 0
                        ? statuses.reduce((top, current) =>
                              STATUS_PRIORITIES[current.requestStatus] >
                              STATUS_PRIORITIES[top.requestStatus]
                                  ? current
                                  : top,
                          ).requestStatus
                        : '-';

                const isStatusMatched =
                    filterConfig.status.length === 0 || filterConfig.status.includes(finalStatus);

                const isSearchKeyMatched =
                    filterConfig.searchKey === '' ||
                    clientInfo.contact?.signingName
                        ?.toLowerCase()
                        ?.includes(filterConfig.searchKey.toLowerCase());

                const isIDVmatch =
                    filterConfig.searchKey === '' ||
                    clientInfo.prospectName
                        ?.toLowerCase()
                        ?.includes(filterConfig.searchKey.toLowerCase());

                const isCFNMatch = clientInfo.customFileNumber
                    ?.toLowerCase()
                    ?.includes(filterConfig.searchKey.toLowerCase());

                return isStatusMatched && (isSearchKeyMatched || isIDVmatch || isCFNMatch);
            }),
        [saidvList, filterConfig],
    );

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const formItemStyles = {
        backgroundColor: theme.palette.neutral.N_50,
        height: '40px',
        boxSizing: 'border-box',
        borderRadius: '8px',
        color: theme.palette.neutral.gray,
    };

    const handleFilterConfigChange = (ev: any, keyName: string) => {
        const {
            target: { value },
        } = ev;
        setFilterConfig({
            ...filterConfig,
            [keyName]: value,
        });
    };

    const handleMultiSelectFilterConfigChange = (ev: any, keyname: string) => {
        const {
            target: { value },
        } = ev;
        if (value === undefined || value.includes(undefined)) {
            return;
        } else {
            const newValue =
                typeof value === 'string'
                    ? [value]
                    : value?.filter((val: any) => val !== undefined);
            setFilterConfig({
                ...filterConfig,
                [keyname]: newValue,
            });
        }
    };

    const columns: Column<StandaloneIDVEntry>[] = [
        {
            id: 'prospectName',
            label: 'IDV ID',
            _render: (row) => (
                <Link style={{ fontWeight: '500' }} to={`/idv/${row.prospectId}/id-verification`}>
                    {row.prospectName}
                </Link>
            ),
            _isSortable: true,
        },
        {
            id: 'customFileNumber',
            label: 'Custom File No.',
            _render: (row) => truncate(row.customFileNumber ?? '', 25),
        },
        {
            id: 'clientName',
            label: 'Client Name',
            _render: (row) => row.contact?.signingName || '-',
            _isSortable: true,
        },
        {
            id: 'user',
            label: 'Assigned User',
            _render: (row) => row.quoteApprover?.fullName || '-',
            _isSortable: true,
        },
        {
            id: 'createdAt',
            label: 'Created on',
            _render: (row) => convertUTCDateToEST(row.createdAt, 'MMMM d, yyyy'),
            _isSortable: true,
        },
        {
            id: 'status',
            label: 'Status',
            _render: (row) => {
                const statuses = row.treefortRequestStatuses;

                const finalStatus =
                    statuses.length > 0
                        ? statuses.reduce((top, current) =>
                              STATUS_PRIORITIES[current.requestStatus] >
                              STATUS_PRIORITIES[top.requestStatus]
                                  ? current
                                  : top,
                          ).requestStatus
                        : '-';

                return (
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 'bold',
                            color: STATUS_COLORS[finalStatus] ?? null,
                        }}
                    >
                        {finalStatus}
                    </Typography>
                );
            },
            _isSortable: true,
        },
    ];

    const filterChipStyles = {
        fontWeight: '600',
        backgroundColor: theme.palette.primary.P_50,
        padding: '6px 4px 6px 10px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        borderRadius: '4px',
    };

    const renderFilterChips = (
        filterConfig: { searchKey: string; status: string[] },
        clearFilterConfig: (key: string, value?: string) => void,
        statusOptions: { name: string; label: string }[],
    ) => {
        const filterSelected: ReactNode[] = [];

        // --- Handle searchKey ---
        if (filterConfig.searchKey?.trim() !== '') {
            filterSelected.push(
                <Box key="searchKey" sx={filterChipStyles}>
                    <Typography variant="h4" sx={{ lineHeight: '18px' }}>
                        {filterConfig.searchKey}
                    </Typography>
                    <IconButton
                        onClick={() => clearFilterConfig('searchKey')}
                        sx={{ color: '#16171A' }}
                        size="small"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>,
            );
        }

        // --- Handle status multi-select ---
        if (Array.isArray(filterConfig.status) && filterConfig.status.length > 0) {
            filterConfig.status.forEach((val) => {
                const label = statusOptions.find((st) => st.name === val)?.label || val;
                filterSelected.push(
                    <Box key={`status-${val}`} sx={filterChipStyles}>
                        <Typography variant="h4" sx={{ lineHeight: '18px' }}>
                            {label}
                        </Typography>
                        <IconButton
                            onClick={() => clearFilterConfig('status', val)}
                            sx={{ color: '#16171A' }}
                            size="small"
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>,
                );
            });
        }

        return filterSelected;
    };

    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const isDeleteButtonDisabled = selectedRows.length === 0;
    const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [isIDVModalOpen, setIsIDVModalOpen] = useState(false);

    const deleteSelectedProspects = async () => {
        setIsDeleteLoading(true);
        try {
            await deleteSaIdv(selectedRows);
            await invalidateSAIDVList(queryClient);
            setOpenDeleteDialog(false);
            setSelectedRows([]);
            successSnackbar('Prospect(s) deleted successfully');
        } catch (error) {
            console.error(error);
        } finally {
            setIsDeleteLoading(false);
            setOpenDeleteDialog(false);
        }
    };

    return (
        <PageWrapper
            title="ID Verification"
            actions={
                <Button variant="contained" onClick={() => setIsIDVModalOpen(true)} size="medium">
                    SEND ID VERIFICATION
                </Button>
            }
        >
            <Stack spacing={2}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <TextField
                            sx={{ ...formItemStyles, width: '400px' }}
                            placeholder="Search by ID, file no. or name"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            value={filterConfig?.searchKey}
                            onChange={(ev) => handleFilterConfigChange(ev, 'searchKey')}
                        />
                        <FormControl
                            sx={{
                                ...formItemStyles,
                                minWidth: '120px',
                            }}
                        >
                            <InputLabel id="status-label">Status</InputLabel>
                            <Select
                                labelId="status-label"
                                id="status"
                                label="Status"
                                multiple
                                value={filterConfig.status}
                                renderValue={(selected) => `Status (${selected?.length})`}
                                onChange={(ev) => handleMultiSelectFilterConfigChange(ev, 'status')}
                            >
                                {/* Select All Option */}
                                <MenuItem
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();

                                        if (filterConfig.status?.length === STATUS_OPTIONS.length) {
                                            setFilterConfig({ ...filterConfig, status: [] });
                                        } else {
                                            setFilterConfig({
                                                ...filterConfig,
                                                status: STATUS_OPTIONS.map((op) => op.name),
                                            });
                                        }
                                    }}
                                >
                                    <Checkbox
                                        checked={
                                            filterConfig.status?.length === STATUS_OPTIONS.length
                                        }
                                        indeterminate={
                                            filterConfig.status?.length > 0 &&
                                            filterConfig.status?.length < STATUS_OPTIONS.length
                                        }
                                    />
                                    <ListItemText
                                        primary={`Select all (${STATUS_OPTIONS.length})`}
                                    />
                                </MenuItem>

                                {/* Individual Status Options */}
                                {STATUS_OPTIONS.map((option) => (
                                    <MenuItem key={option.name} value={option.name}>
                                        <Checkbox
                                            checked={filterConfig.status?.includes(option.name)}
                                        />
                                        <ListItemText primary={option.label} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        {!isDeleteButtonDisabled && (
                            <LoadingButton
                                loading={isDeleteLoading}
                                variant="contained"
                                onClick={() => setOpenDeleteDialog(true)}
                                disabled={isDeleteButtonDisabled}
                                size="medium"
                            >
                                Archive
                            </LoadingButton>
                        )}
                    </Stack>
                </Box>
                <Stack direction="row" spacing={2}>
                    {renderFilterChips(filterConfig, clearFilterConfig, STATUS_OPTIONS)}
                </Stack>
                <DataTable
                    generateKeyFrom="prospectId"
                    columns={columns}
                    rows={filteredIDV}
                    isLoading={isLoading}
                    customStyles={{
                        border: `1px solid ${theme.palette.neutral.borderColor}`,
                        boxShadow: 'none',
                    }}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    isSelectable
                    selected={selectedRows}
                    setSelected={setSelectedRows}
                />
            </Stack>
            <AlertDialog
                open={openDeleteDialog}
                isLoading={isDeleteLoading}
                handleClose={() => setOpenDeleteDialog(false)}
                title={`Are you sure you want to delete the ${
                    selectedRows?.length === 1 ? 'id verification' : 'id verification(s)'
                }?`}
                message={
                    selectedRows?.length === 1
                        ? 'By clicking Accept you will delete the selected id verification. To recover this id verification, you will need to contact Lexo Support.'
                        : `By clicking Accept you will delete ${selectedRows?.length} id verification(s). To recover these prospects, you will need to contact Lexo Support.`
                }
                handleConfirm={deleteSelectedProspects}
            />
            {isIDVModalOpen && <IDVModal onClose={() => setIsIDVModalOpen(false)} />}
        </PageWrapper>
    );
}
